// ─────────────────────────────────────────────────────────────────
// Glass-i — Concept Gallery Card (Stage 6B.8A-3I).
//
// One card per concept. Used by the gallery view of GlassConceptsPage.
// Hover lifts the card; the bottom action bar exposes the four
// primary actions (View / Edit / Use / Duplicate).
// ─────────────────────────────────────────────────────────────────

import { Tag, Button, Tooltip, Card } from 'antd';
import {
  EyeOutlined, EditOutlined, ToolOutlined,
  CopyOutlined, FileImageOutlined
} from '@ant-design/icons';
import { CONCEPT_TYPE_OPTIONS } from '../glassConceptsApi';

function typeLabel(value) {
  const opt = CONCEPT_TYPE_OPTIONS.find((o) => o.value === value);
  return opt ? opt.label : value;
}

export default function ConceptGalleryCard({
  concept, onView, onEdit, onUse, onDuplicate
}) {
  const img = concept.thumbnailUrl || concept.imageUrl || null;

  return (
    <Card
      hoverable
      size="small"
      style={{
        borderRadius: 10,
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column', flex: 1 }
      }}
    >
      {/* Hero image */}
      <div
        onClick={() => onView && onView(concept)}
        style={{
          background: '#f1f5f9',
          height: 140,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          cursor: 'pointer'
        }}
      >
        {img ? (
          <img
            src={img}
            alt={concept.conceptName}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div style={{ color: '#94a3b8', textAlign: 'center', fontSize: 12 }}>
            <FileImageOutlined style={{ fontSize: 28, display: 'block', marginBottom: 4 }} />
            No image
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: '10px 12px', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <code style={{
            fontSize: 11, color: '#475569', background: '#f1f5f9',
            padding: '1px 6px', borderRadius: 3, fontWeight: 500
          }}>{concept.conceptCode}</code>
          {concept.isActive
            ? <Tag color="green" style={{ margin: 0, fontSize: 10, lineHeight: '16px' }}>Active</Tag>
            : <Tag color="default" style={{ margin: 0, fontSize: 10, lineHeight: '16px' }}>Inactive</Tag>}
        </div>
        <div
          onClick={() => onView && onView(concept)}
          style={{
            fontWeight: 600, fontSize: 13.5, color: '#0f172a',
            lineHeight: 1.3, marginBottom: 6, cursor: 'pointer',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {concept.conceptName}
        </div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
          <Tag color="geekblue" style={{ margin: 0, fontSize: 10, lineHeight: '16px' }}>
            {typeLabel(concept.conceptType)}
          </Tag>
          {concept.designCategory ? (
            <Tag color="default" style={{ margin: 0, fontSize: 10, lineHeight: '16px' }}>
              {concept.designCategory}
            </Tag>
          ) : null}
        </div>
        <div style={{ fontSize: 11.5, color: '#64748b' }}>
          {(concept.panelCount || 0)} panel{concept.panelCount === 1 ? '' : 's'}
          {' · '}
          {(concept.hardwareCount || 0)} hardware
          {(concept.serviceCount || 0) > 0
            ? ` · ${concept.serviceCount} service`
            : ''}
        </div>
      </div>

      {/* Actions */}
      <div style={{
        borderTop: '1px solid #e2e8f0',
        background: '#f8fafc',
        padding: '6px 8px',
        display: 'flex',
        justifyContent: 'space-between',
        gap: 4
      }}>
        <Tooltip title="View details">
          <Button size="small" type="text" icon={<EyeOutlined />}
            onClick={() => onView && onView(concept)} />
        </Tooltip>
        <Tooltip title="Edit concept">
          <Button size="small" type="text" icon={<EditOutlined />}
            onClick={() => onEdit && onEdit(concept)} />
        </Tooltip>
        <Tooltip title="Use in Quotation">
          <Button size="small" type="primary" icon={<ToolOutlined />}
            onClick={() => onUse && onUse(concept)}>
            Use
          </Button>
        </Tooltip>
        <Tooltip title="Duplicate concept">
          <Button size="small" type="text" icon={<CopyOutlined />}
            onClick={() => onDuplicate && onDuplicate(concept)} />
        </Tooltip>
      </div>
    </Card>
  );
}
